import React, { useState, useRef } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faCheckCircle, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import './AiDraftModal.css';
import { Language_List } from '../helper/Language_Utils';

const AiDraftModal = ({ show, onHide, onGenerate, generationStatus }) => {
  const [file, setFile] = useState(null);
  const [language, setLanguage] = useState('en'); // Default to English
  const [comments, setComments] = useState('');
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef(null);

  const languages = Language_List.sort((a, b) => a.name.localeCompare(b.name));

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
    } else {
      alert("Please drop a valid PDF file.");
    }
  };
  
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
     if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
    } else {
      alert("Please select a valid PDF file.");
    }
  };

  const handleGenerateClick = () => {
    if (file) {
      onGenerate(file, language);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setLanguage('en');
    onHide();
  };

  const getGenerationStatusMessage = (status) => {
    switch (status) {
      case 'preparing':
        return 'Preparing...';
      case 'uploading':
        return 'Uploading...';
      case 'processing':
        return 'Processing...';
      case 'done':
        return 'Done!';
      default:
        return 'Generating...';
    }
  };

  return (
    <Modal show={show} onHide={handleCancel} centered size="lg" backdrop={generationStatus ? 'static' : true} keyboard={!generationStatus}>
      <Modal.Header closeButton={!generationStatus}>
        <Modal.Title>AI Draft from PDF</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {generationStatus ? (
          <div className="ai-draft-loader-container">
            {generationStatus === 'done' ? (
              <>
                <FontAwesomeIcon icon={faCheckCircle} size="3x" className="mb-3 text-success" />
                <p className="ai-draft-loader-text mb-2">Done!</p>
                <small className="text-muted text-center">
                  Please carefully review the draft for factual content. AI can generate inaccuracies.
                </small>
              </>
            ) : (
              <>
                <div className="ai-draft-loader"></div>
                <p className="ai-draft-loader-text">{getGenerationStatusMessage(generationStatus)}</p>
              </>
            )}
          </div>
        ) : (
          <>
            <p className="text-muted">Upload a scientific paper to get a head start on your scrollytelling story. The AI will analyze the content and suggest a narrative structure with visuals.</p>
            
            <Form.Group className="mb-3">
              <Form.Label><strong>Translation Language</strong></Form.Label>
              <Form.Select value={language} onChange={(e) => setLanguage(e.target.value)} disabled={!!generationStatus}>
                {languages.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                The AI will generate the story in the selected language.
              </Form.Text>
            </Form.Group>
            
            <div
              className={`drop-zone ${isDraggingOver ? 'dragging-over' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept=".pdf"
              />
              {file ? (
                <div className="text-center">
                  <FontAwesomeIcon icon={faFilePdf} size="3x" className="mb-3 text-success" />
                  <p className="fw-bold mb-0">{file.name}</p>
                  <small className="text-muted">{(file.size / 1024).toFixed(2)} KB</small>
                </div>
              ) : (
                <div className="text-center">
                  <p><strong>Drag & drop a PDF file here</strong></p>
                  <p className="text-muted mb-0">or click to select a file</p>
                </div>
              )}
            </div>

            {/*
              To add back the "Optional Comments" field, remove the block comment around this Form.Group.
              This field allows users to provide hints to the AI.
              You will also need to handle the 'comments' state in the handleGenerateClick function.
            <Form.Group className="mt-4">
              <Form.Label>Optional Comments</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="e.g., 'Focus on the results section', 'Keep the tone informal', 'Emphasize the new methodology'"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                disabled={!!generationStatus}
              />
            </Form.Group>
            */}
          </>
        )}
      </Modal.Body>
      
      {/* Show different footer based on status */}
      {generationStatus === 'done' ? (
        <Modal.Footer>
          <Button variant="primary" onClick={handleCancel}>
            View Draft
          </Button>
        </Modal.Footer>
      ) : !generationStatus ? (
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleGenerateClick} 
            disabled={!file}
            style={{ minWidth: '150px' }}
          >
            Generate Story
          </Button>
        </Modal.Footer>
      ) : null}
    </Modal>
  );
};

export default AiDraftModal; 